import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { AutodichiarazioneEmployeePicker } from "./AutodichiarazioneEmployeePicker";

const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const MESI = ["", "Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const monthShort = (m) => `${MESI[Number(m.slice(5, 7))]} ${m.slice(2, 4)}`;

/** Elenco nominativo con controllo buste paga per mese; barra fissa in alto per aggiungere/escludere dipendenti. */
export function AutodichiarazionePreview({ preview, excluded, onToggle, onAdd, adding }) {
    const included = preview.employees.filter((e) => !excluded.includes(e.id));
    const missing = included.reduce((n, e) => n + e.months_missing.length, 0);
    const ceased = included.filter((e) => e.data_cessazione).length;
    return (
        <div data-testid="ad-preview">
            <div className="sticky top-14 z-10 -mx-5 mb-3 space-y-2 border-b border-slate-100 bg-white px-5 py-3 lg:top-0" data-testid="ad-preview-toolbar">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div className="text-slate-700">
                        <b data-testid="ad-preview-count">{included.length}</b> dipendenti in forza nel periodo ({preview.periodo_label})
                        {ceased > 0 && <span className="ml-1 text-slate-500" data-testid="ad-preview-ceased">· di cui {ceased} cessat{ceased === 1 ? "o" : "i"} nel periodo</span>}
                        {excluded.length > 0 && <span className="ml-1 text-slate-400">· {excluded.length} esclusi</span>}
                    </div>
                    {missing > 0 ? (
                        <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700" data-testid="ad-preview-missing">
                            <AlertTriangle className="h-3.5 w-3.5" /> {missing} buste paga non presenti in archivio: verifica prima di dichiarare
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid="ad-preview-ok">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Tutte le buste paga del periodo sono in archivio
                        </div>
                    )}
                </div>
                {onAdd && (
                    <div className="flex flex-wrap items-center gap-2">
                        <AutodichiarazioneEmployeePicker employees={preview.addable || []} onAdd={onAdd} disabled={adding} />
                        <span className="text-[11px] text-slate-500">Puoi aggiungere qualsiasi dipendente, attivo o cessato, in forza nel periodo; usa la spunta per escludere chi non c'entra.</span>
                    </div>
                )}
            </div>
            <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full min-w-[720px] text-xs">
                    <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                        <tr>
                            <th className="px-2 py-2 w-8"></th>
                            <th className="px-2 py-2">Dipendente</th>
                            <th className="px-2 py-2">Qualifica</th>
                            <th className="px-2 py-2">In forza</th>
                            {preview.months.map((m) => <th key={m} className="px-1 py-2 text-center font-mono-numbers">{monthShort(m)}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {preview.employees.map((e) => {
                            const off = excluded.includes(e.id);
                            return (
                                <tr key={e.id} className={`border-t border-slate-100 ${off ? "opacity-40" : ""}`} data-testid={`ad-emp-${e.id}`}>
                                    <td className="px-2 py-1.5">
                                        <input type="checkbox" checked={!off} onChange={() => onToggle(e.id)} className="h-3.5 w-3.5 accent-[#376DB3]" data-testid={`ad-emp-toggle-${e.id}`} title={off ? "Includi" : "Escludi dall'elenco"} />
                                    </td>
                                    <td className="px-2 py-1.5 font-semibold text-slate-700">{e.cognome} {e.nome} <span className="font-normal text-slate-400">· {e.codice_fiscale || "CF mancante"}</span></td>
                                    <td className="px-2 py-1.5 text-slate-600">{e.qualifica || "—"}</td>
                                    <td className="px-2 py-1.5 font-mono-numbers text-slate-600">
                                        {fmtD(e.data_assunzione)}
                                        {e.data_cessazione && <span className="ml-1 rounded bg-slate-100 px-1 py-px text-[10px] font-semibold uppercase text-slate-600" data-testid={`ad-emp-ceased-${e.id}`}>cessato {fmtD(e.data_cessazione)}</span>}
                                    </td>
                                    {preview.months.map((m) => {
                                        const expected = e.months_expected.includes(m);
                                        const miss = e.months_missing.includes(m);
                                        return (
                                            <td key={m} className="px-1 py-1.5 text-center">
                                                {!expected ? <span className="text-slate-300">·</span> : miss
                                                    ? <span className="inline-block rounded bg-amber-100 px-1 font-bold text-amber-700" title="Busta paga non in archivio">✕</span>
                                                    : <span className="font-bold text-emerald-600" title="Busta paga presente">✓</span>}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
